import type { GridColDef } from '@mui/x-data-grid';
import { EmptyTableCell } from '@/components/EmptyTableCell';
import { formatAmount, formatPercentage } from '../../helpers';
import type {
  GeographyGroupBy,
  GeographyPerformanceRow,
} from '../hooks/useGeographyPerformance';
import { PercentageCellWithBackground } from './PercentageCellWithBackground';

export type { GeographyPerformanceRow } from '../hooks/useGeographyPerformance';

const metricColumns: GridColDef<GeographyPerformanceRow>[] = [
  {
    field: 'totalTransactions',
    headerName: 'Total Transactions',
    minWidth: 150,
    flex: 1,
  },
  {
    field: 'salesVolume',
    headerName: 'Sales Volume',
    valueFormatter: formatAmount,
    minWidth: 160,
    flex: 1,
  },
  {
    field: 'averageSaleValue',
    headerName: 'Avg Sale Value',
    valueFormatter: formatAmount,
    minWidth: 160,
    flex: 1,
  },
  {
    field: 'adjustedGci',
    headerName: 'Post-Referral GCI',
    valueFormatter: formatAmount,
    minWidth: 160,
    flex: 1,
  },
  {
    field: 'averageCommissionAmount',
    headerName: 'Average Commission',
    renderCell: ({ row }) =>
      row.averageCommissionAmount != null ? (
        formatAmount(row.averageCommissionAmount)
      ) : (
        <EmptyTableCell />
      ),
    valueFormatter: formatAmount,
    flex: 1,
    minWidth: 170,
  },
  {
    field: 'salesVolumePercent',
    headerName: '% of Total Sales Volume',
    minWidth: 220,
    flex: 1,
    valueFormatter: formatPercentage,
    renderCell: (params) => (
      <PercentageCellWithBackground
        total={params.row.totalSalesVolume}
        currentValue={params.row.salesVolume}
        backgroundColorFilled="primary.500"
        backgroundColorEmpty="primary.300"
      />
    ),
  },
];

const cityStateColumns: GridColDef<GeographyPerformanceRow>[] = [
  {
    field: 'city',
    headerName: 'City',
    minWidth: 140,
    flex: 1,
    valueGetter: (value: string | null) => value ?? '—',
  },
  {
    field: 'state',
    headerName: 'State',
    minWidth: 100,
    flex: 1,
    valueGetter: (value: string | null) => value ?? '—',
  },
];

const zipCodeColumn: GridColDef<GeographyPerformanceRow>[] = [
  {
    field: 'zipCode',
    headerName: 'Zip Code',
    minWidth: 120,
    flex: 1,
    valueGetter: (value: string | null) => value ?? '—',
  },
];

/**
 * Returns column definitions for the Geography Performance grid based on groupBy.
 * CITY: City, State, then metrics. ZIP_CODE: Zip Code, City, State, then metrics.
 */
export function getGeographyPerformanceColumns(
  groupedBy: GeographyGroupBy,
): GridColDef<GeographyPerformanceRow>[] {
  if (groupedBy === 'ZIP_CODE') {
    return [...zipCodeColumn, ...cityStateColumns, ...metricColumns];
  }
  return [...cityStateColumns, ...metricColumns];
}

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
export const DEFAULT_PAGE_SIZE = 10;
